/**
 * Turns widget configs + the dataset bag into render-ready numbers.
 *
 * Everything here is pure and synchronous so a widget re-evaluates for free
 * whenever the window or the bag changes, and so the same code paths can
 * be exercised without a browser.
 */

import { CHART_SERIES } from '@/lib/brand';
import type { Catalog } from './catalog';
import type { BreakdownSlice, ForecastSourceDef, MetricDef, TableColumnDef, TableDef, TableRowData } from './catalogTypes';
import type { DatasetBag } from './datasets';
import { forecastSeries, impliedGrowth, projectBalance, zeroCrossing, type ForecastResult } from './forecasting';
import type { DashboardWindow, ForecastHistory, ForecastHorizon } from './range';
import {
  addMonths,
  currentMonthKey,
  eachBucket,
  monthKey,
  pctDelta,
  rollup,
  sliceWindow,
  sliceWindowMonths,
  todayIso,
  type Aggregate,
} from './series';
import {
  isBreakdownChart,
  type ChartWidget,
  type ForecastMethod,
  type ForecastPoint,
  type ForecastWidget,
  type SeriesPoint,
  type TableWidget,
  type TimeGrain,
  type Unit,
} from './types';

export function isReady(def: { requires: readonly string[] }, bag: DatasetBag): boolean {
  return def.requires.every((id) => (bag as Record<string, unknown>)[id] !== undefined);
}

function aggOrNull(values: number[], agg: Aggregate): number | null {
  if (values.length === 0) return null;
  switch (agg) {
    case 'sum':
      return values.reduce((s, v) => s + v, 0);
    case 'avg':
      return values.reduce((s, v) => s + v, 0) / values.length;
    case 'max':
      return Math.max(...values);
    case 'last':
      return values[values.length - 1];
  }
}

function pointsIn(def: MetricDef, bag: DatasetBag, start: string, end: string): SeriesPoint[] {
  const series = def.series?.(bag) ?? [];
  return def.grain === 'month' ? sliceWindowMonths(series, start, end) : sliceWindow(series, start, end);
}

/** Last value strictly before `start` — seeds carry-forward fills for level metrics. */
function seedBefore(def: MetricDef, bag: DatasetBag, start: string): number | null {
  const series = def.series?.(bag) ?? [];
  const cutoff = def.grain === 'month' ? monthKey(start) : start;
  let seed: number | null = null;
  for (const p of series) {
    if (p.t < cutoff) seed = p.v;
    else break;
  }
  return seed;
}

// ---------------------------------------------------------------------------
// Metrics
// ---------------------------------------------------------------------------

export interface MetricResult {
  def: MetricDef;
  value: number | null;
  previous: number | null;
  /** Relative change vs the previous window (0.12 = +12%). */
  delta: number | null;
  sparkline: SeriesPoint[];
  grain: TimeGrain;
  /** Explanation when no value can be produced even though data loaded. */
  unavailable: string | null;
  ready: boolean;
}

export function metricWindowValue(
  def: MetricDef,
  bag: DatasetBag,
  start: string,
  end: string,
  catalog: Catalog,
  depth = 0,
): number | null {
  if (def.ratio) {
    if (depth > 3) return null;
    const num = catalog.metric(def.ratio.num);
    const den = catalog.metric(def.ratio.den);
    if (!num || !den) return null;
    const n = metricWindowValue(num, bag, start, end, catalog, depth + 1);
    const d = metricWindowValue(den, bag, start, end, catalog, depth + 1);
    if (n == null || d == null || d === 0) return null;
    return n / d;
  }
  switch (def.grain) {
    case 'day':
    case 'month':
      return aggOrNull(
        pointsIn(def, bag, start, end).map((p) => p.v),
        def.agg,
      );
    case 'snapshot':
      return def.snapshot?.(bag) ?? null;
    case 'window':
      return def.windowValue?.(bag, start, end) ?? null;
  }
}

/** Effective chart grain for a set of metrics: month-grain sources force months. */
export function effectiveGrain(defs: MetricDef[], requested: TimeGrain): TimeGrain {
  return defs.some((d) => d.grain === 'month') ? 'month' : requested;
}

export function bucketsFor(start: string, end: string, grain: TimeGrain): string[] {
  return grain === 'month' ? eachBucket(monthKey(start), monthKey(end), 'month') : eachBucket(start, end, grain);
}

/**
 * Values of a metric aligned to `buckets`. Sum-style metrics fill gaps with
 * 0; level/average metrics carry forward (or stay null before any data).
 */
export function metricBucketValues(
  def: MetricDef,
  bag: DatasetBag,
  buckets: string[],
  grain: TimeGrain,
  catalog: Catalog,
  depth = 0,
): Array<number | null> {
  if (buckets.length === 0) return [];
  if (def.ratio) {
    if (depth > 3) return buckets.map(() => null);
    const num = catalog.metric(def.ratio.num);
    const den = catalog.metric(def.ratio.den);
    if (!num || !den) return buckets.map(() => null);
    const n = metricBucketValues(num, bag, buckets, grain, catalog, depth + 1);
    const d = metricBucketValues(den, bag, buckets, grain, catalog, depth + 1);
    return buckets.map((_, i) => {
      const nv = n[i];
      const dv = d[i];
      return nv == null || dv == null || dv === 0 ? null : nv / dv;
    });
  }
  if (def.grain === 'snapshot' || def.grain === 'window') return buckets.map(() => null);

  const start = buckets[0];
  const end = grain === 'month' ? addMonths(buckets[buckets.length - 1], 1) : buckets[buckets.length - 1];
  const raw = pointsIn(def, bag, start, end);
  const rolled = rollup(raw, grain, def.agg);
  const byKey = new Map(rolled.map((p) => [p.t, p.v]));
  if (def.agg === 'sum') return buckets.map((t) => byKey.get(t) ?? 0);
  let carry: number | null = def.agg === 'last' ? seedBefore(def, bag, start) : null;
  return buckets.map((t) => {
    const v = byKey.get(t);
    if (v !== undefined) {
      carry = v;
      return v;
    }
    return def.agg === 'last' ? carry : null;
  });
}

export function evaluateMetric(
  def: MetricDef,
  bag: DatasetBag,
  window: DashboardWindow,
  catalog: Catalog,
  grain: TimeGrain = window.grain,
): MetricResult {
  const ready = isReady(def, bag) && ratioReady(def, bag, catalog);
  const chartGrain = effectiveGrain([def], grain);
  if (!ready) {
    return { def, value: null, previous: null, delta: null, sparkline: [], grain: chartGrain, unavailable: null, ready };
  }
  const unavailable = def.available?.(bag) ?? null;
  if (unavailable) {
    return { def, value: null, previous: null, delta: null, sparkline: [], grain: chartGrain, unavailable, ready };
  }
  const value = metricWindowValue(def, bag, window.currentStart, window.currentEnd, catalog);
  const previous =
    def.grain === 'snapshot' ? null : metricWindowValue(def, bag, window.previousStart, window.previousEnd, catalog);
  const buckets = bucketsFor(window.currentStart, window.currentEnd, chartGrain);
  const values = metricBucketValues(def, bag, buckets, chartGrain, catalog);
  const sparkline = buckets
    .map((t, i) => ({ t, v: values[i] }))
    .filter((p): p is SeriesPoint => p.v != null);
  return {
    def,
    value,
    previous,
    delta: value == null ? null : pctDelta(value, previous),
    sparkline,
    grain: chartGrain,
    unavailable: null,
    ready,
  };
}

function ratioReady(def: MetricDef, bag: DatasetBag, catalog: Catalog, depth = 0): boolean {
  if (!def.ratio || depth > 3) return true;
  const num = catalog.metric(def.ratio.num);
  const den = catalog.metric(def.ratio.den);
  if (!num || !den) return false;
  return (
    isReady(num, bag) && isReady(den, bag) && ratioReady(num, bag, catalog, depth + 1) && ratioReady(den, bag, catalog, depth + 1)
  );
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------

export interface ChartSeriesMeta {
  key: string;
  label: string;
  color: string;
  unit: Unit;
  kind: 'bar' | 'line' | 'area';
  axis: 'left' | 'right';
  stackId?: string;
  /** Key of the prior-period column, when compare is on. */
  previousKey?: string;
}

export type ChartRow = Record<string, string | number | null>;

export interface ChartResult {
  mode: 'series' | 'breakdown';
  grain: TimeGrain;
  rows: ChartRow[];
  series: ChartSeriesMeta[];
  unitLeft: Unit;
  unitRight: Unit | null;
  slices: BreakdownSlice[];
  sliceUnit: Unit;
  ready: boolean;
  unavailable: string | null;
  isEmpty: boolean;
}

const DEFAULT_SLICE_LIMIT = 8;

export function foldSlices(slices: BreakdownSlice[], limit: number): BreakdownSlice[] {
  const sorted = [...slices].sort((a, b) => b.value - a.value);
  if (sorted.length <= limit) return sorted;
  const head = sorted.slice(0, limit - 1);
  const rest = sorted.slice(limit - 1).reduce((s, x) => s + x.value, 0);
  return [...head, { label: 'Other', value: rest }];
}

export function evaluateChart(widget: ChartWidget, bag: DatasetBag, window: DashboardWindow, catalog: Catalog): ChartResult {
  const base: ChartResult = {
    mode: isBreakdownChart(widget.chartType) ? 'breakdown' : 'series',
    grain: window.grain,
    rows: [],
    series: [],
    unitLeft: 'count',
    unitRight: null,
    slices: [],
    sliceUnit: 'count',
    ready: false,
    unavailable: null,
    isEmpty: true,
  };

  if (base.mode === 'breakdown') {
    const def = widget.breakdown ? catalog.byId.get(widget.breakdown) : undefined;
    if (!def || def.kind !== 'breakdown') {
      return { ...base, ready: true, unavailable: widget.breakdown ? 'This breakdown is not available to you.' : 'Pick a breakdown to chart.' };
    }
    if (!isReady(def, bag)) return { ...base, sliceUnit: def.unit };
    const slices = foldSlices(def.slices(bag, window.currentStart, window.currentEnd), widget.limit ?? DEFAULT_SLICE_LIMIT);
    return { ...base, ready: true, slices, sliceUnit: def.unit, isEmpty: slices.length === 0 };
  }

  const configs = widget.series.filter((s) => catalog.metric(s.metric));
  if (configs.length === 0) {
    return {
      ...base,
      ready: true,
      unavailable: widget.series.length ? 'None of the selected metrics are available to you.' : 'Pick at least one metric to chart.',
    };
  }
  const defs = configs.map((s) => catalog.metric(s.metric)!);
  if (!defs.every((d) => isReady(d, bag) && ratioReady(d, bag, catalog))) return base;

  const chartable = defs.filter((d) => d.grain === 'day' || d.grain === 'month' || d.ratio);
  if (chartable.length === 0) {
    return { ...base, ready: true, unavailable: 'Point-in-time metrics have no history to chart — try a metric tile instead.' };
  }

  const grain = effectiveGrain(defs, widget.grain === 'auto' ? window.grain : widget.grain);
  const buckets = bucketsFor(window.currentStart, window.currentEnd, grain);
  const prevBuckets = widget.compare ? bucketsFor(window.previousStart, window.previousEnd, grain) : [];
  const unitLeft = defs[0].unit;
  let unitRight: Unit | null = null;

  const series: ChartSeriesMeta[] = [];
  const columns: Array<Array<number | null>> = [];
  const prevColumns: Array<Array<number | null> | null> = [];

  defs.forEach((def, i) => {
    const cfg = configs[i];
    let values = metricBucketValues(def, bag, buckets, grain, catalog);
    let previous = widget.compare && prevBuckets.length ? metricBucketValues(def, bag, prevBuckets, grain, catalog) : null;
    if (widget.cumulative) {
      values = runningTotal(values);
      previous = previous ? runningTotal(previous) : null;
    }
    let axis: 'left' | 'right' = cfg.axis ?? 'left';
    if (!cfg.axis && def.unit !== unitLeft) axis = 'right';
    if (axis === 'right') unitRight = unitRight ?? def.unit;

    const kind: ChartSeriesMeta['kind'] =
      widget.chartType === 'combo'
        ? (cfg.kind ?? (i === 0 ? 'bar' : 'line'))
        : widget.chartType === 'bar' || widget.chartType === 'stacked'
          ? 'bar'
          : widget.chartType === 'area'
            ? 'area'
            : 'line';

    series.push({
      key: `s${i}`,
      label: def.label,
      color: cfg.color ?? CHART_SERIES[i % CHART_SERIES.length],
      unit: def.unit,
      kind,
      axis,
      stackId: widget.chartType === 'stacked' ? 'stack' : undefined,
      previousKey: previous ? `p${i}` : undefined,
    });
    columns.push(values);
    prevColumns.push(previous);
  });

  const rows: ChartRow[] = buckets.map((t, bi) => {
    const row: ChartRow = { t };
    series.forEach((s, si) => {
      row[s.key] = columns[si][bi];
      if (s.previousKey) row[s.previousKey] = prevColumns[si]?.[bi] ?? null;
    });
    return row;
  });

  const isEmpty = columns.every((col) => col.every((v) => v == null || v === 0));
  return { ...base, grain, rows, series, unitLeft, unitRight, ready: true, isEmpty };
}

function runningTotal(values: Array<number | null>): Array<number | null> {
  let run = 0;
  let seen = false;
  return values.map((v) => {
    if (v == null && !seen) return null;
    seen = true;
    run += v ?? 0;
    return run;
  });
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export interface TableResult {
  def: TableDef | null;
  columns: TableColumnDef[];
  rows: TableRowData[];
  total: number;
  ready: boolean;
  unavailable: string | null;
}

function compareCells(a: TableRowData, b: TableRowData, key: string, dir: 'asc' | 'desc'): number {
  const av = a.cells[key];
  const bv = b.cells[key];
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  let cmp: number;
  if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
  else cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
  return dir === 'asc' ? cmp : -cmp;
}

export function resolveTableColumns(def: TableDef, selected: string[]): TableColumnDef[] {
  if (selected.length > 0) {
    const byKey = new Map(def.columns.map((c) => [c.key, c]));
    const chosen = selected.map((k) => byKey.get(k)).filter((c): c is TableColumnDef => !!c);
    if (chosen.length > 0) return chosen;
  }
  if (def.defaultColumns) {
    const keep = new Set(def.defaultColumns);
    return def.columns.filter((c) => keep.has(c.key));
  }
  return def.columns;
}

export function evaluateTable(widget: TableWidget, bag: DatasetBag, window: DashboardWindow, catalog: Catalog): TableResult {
  const def = catalog.byId.get(widget.table);
  if (!def || def.kind !== 'table') {
    return { def: null, columns: [], rows: [], total: 0, ready: true, unavailable: 'This table is not available to you.' };
  }
  const columns = resolveTableColumns(def, widget.columns);
  if (!isReady(def, bag)) return { def, columns, rows: [], total: 0, ready: false, unavailable: null };
  const all = def.rows(bag, window.currentStart, window.currentEnd);
  const sort = widget.sort ?? def.defaultSort ?? null;
  const sorted = sort ? [...all].sort((a, b) => compareCells(a, b, sort.key, sort.dir)) : all;
  const limit = widget.limit > 0 ? widget.limit : (def.defaultLimit ?? 10);
  return { def, columns, rows: sorted.slice(0, limit), total: all.length, ready: true, unavailable: null };
}

// ---------------------------------------------------------------------------
// Forecasts
// ---------------------------------------------------------------------------

export interface ForecastSettings {
  horizon: ForecastHorizon;
  history: ForecastHistory;
}

export interface ForecastView {
  def: ForecastSourceDef | null;
  unit: Unit;
  /** Complete months shown on the chart. */
  history: SeriesPoint[];
  /** Month-to-date actual for the current month, if any. */
  partial: SeriesPoint | null;
  forecast: ForecastPoint[];
  method: ForecastResult['method'] | null;
  mape: number | null;
  manual: SeriesPoint[];
  manualLabel: string;
  external: ForecastPoint[];
  externalLabel: string;
  isBalance: boolean;
  horizon: number;
  historyMonths: number;
  summary: {
    last: number | null;
    next: number | null;
    endOfHorizon: number | null;
    horizonTotal: number | null;
    growth: number | null;
    zeroCrossing: string | null;
    fittedMonths: number;
  };
  ready: boolean;
  unavailable: string | null;
}

const MAX_FIT_MONTHS = 36;

function fillMonths(points: SeriesPoint[], from: string, to: string, mode: 'zero' | 'carry'): SeriesPoint[] {
  const byKey = new Map(points.map((p) => [p.t, p.v]));
  let last = 0;
  return eachBucket(from, to, 'month').map((t) => {
    const v = byKey.get(t);
    if (v !== undefined) {
      last = v;
      return { t, v };
    }
    return { t, v: mode === 'carry' ? last : 0 };
  });
}

/** Fraction of the current month still ahead of us (today inclusive). */
function remainingMonthFraction(): number {
  const today = new Date(`${todayIso()}T00:00:00Z`);
  const daysInMonth = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0)).getUTCDate();
  return Math.max(0, daysInMonth - today.getUTCDate() + 1) / daysInMonth;
}

export function evaluateForecast(
  widget: ForecastWidget,
  bag: DatasetBag,
  settings: ForecastSettings,
  catalog: Catalog,
): ForecastView {
  const horizon = widget.horizon > 0 ? widget.horizon : settings.horizon;
  const historyMonths = widget.history > 0 ? widget.history : settings.history;
  const def = catalog.byId.get(widget.source);
  const empty: ForecastView = {
    def: def && def.kind === 'forecast' ? def : null,
    unit: def && def.kind === 'forecast' ? def.unit : 'count',
    history: [],
    partial: null,
    forecast: [],
    method: null,
    mape: null,
    manual: [],
    manualLabel: (def && def.kind === 'forecast' && def.manualLabel) || 'Plan',
    external: [],
    externalLabel: (def && def.kind === 'forecast' && def.externalLabel) || 'Model run',
    isBalance: !!(def && def.kind === 'forecast' && def.balance),
    horizon,
    historyMonths,
    summary: { last: null, next: null, endOfHorizon: null, horizonTotal: null, growth: null, zeroCrossing: null, fittedMonths: 0 },
    ready: false,
    unavailable: null,
  };
  if (!def || def.kind !== 'forecast') return { ...empty, ready: true, unavailable: 'This forecast source is not available to you.' };
  if (def.perItem && !widget.itemId) return { ...empty, ready: true, unavailable: 'Pick an item to forecast.' };
  if (!isReady(def, bag)) return empty;

  const current = currentMonthKey();
  const raw = def.history(bag, widget.itemId);
  const complete = raw.filter((p) => p.t < current);
  const partial = raw.find((p) => p.t === current) ?? null;
  if (complete.length === 0) {
    return { ...empty, ready: true, partial, unavailable: 'Needs at least one complete month of history.' };
  }

  const fill = def.fill ?? 'zero';
  const lastComplete = addMonths(current, -1);
  const filled = fillMonths(complete, complete[0].t, lastComplete, fill);
  const fitInput = filled.slice(-Math.max(MAX_FIT_MONTHS, historyMonths));
  const shown = filled.slice(-historyMonths);

  let forecast: ForecastPoint[];
  let fit: ForecastResult;
  if (def.balance) {
    const start = def.balance.startValue(bag);
    if (start == null) return { ...empty, ready: true, unavailable: 'No current balance to project from.' };
    const flowRaw = def.balance.flow(bag).filter((p) => p.t < current);
    const flow = flowRaw.length ? fillMonths(flowRaw, flowRaw[0].t, lastComplete, 'zero').slice(-MAX_FIT_MONTHS) : [];
    fit = forecastSeries(flow, { method: widget.method as ForecastMethod, horizon, grain: 'month', nonNegative: false });
    // The first projected month is the current one: only the remaining
    // fraction of its flow is still ahead of today's balance.
    const scale = remainingMonthFraction();
    const flows = fit.points.map((p, i) => (i === 0 ? { ...p, v: p.v * scale, lo: p.lo * scale, hi: p.hi * scale } : p));
    forecast = projectBalance(start, flows);
  } else {
    fit = forecastSeries(fitInput, { method: widget.method, horizon, grain: 'month', nonNegative: def.nonNegative });
    forecast = fit.points;
  }

  const firstShown = shown[0]?.t ?? current;
  const lastForecast = forecast[forecast.length - 1]?.t ?? current;
  const manual = widget.showManual
    ? (def.manual?.(bag, widget.itemId) ?? []).filter((p) => p.t >= firstShown && p.t <= lastForecast)
    : [];
  const external = (def.external?.(bag, widget.itemId) ?? []).filter((p) => p.t >= current && p.t <= lastForecast);

  const last = shown[shown.length - 1]?.v ?? null;
  const next = forecast[0]?.v ?? null;
  const endOfHorizon = forecast[forecast.length - 1]?.v ?? null;
  return {
    ...empty,
    history: shown,
    partial,
    forecast,
    method: fit.method,
    mape: fit.mape,
    manual,
    external,
    summary: {
      last,
      next,
      endOfHorizon,
      horizonTotal: def.balance ? null : forecast.reduce((s, p) => s + p.v, 0),
      growth: impliedGrowth(shown),
      zeroCrossing: def.nonNegative ? null : zeroCrossing(forecast),
      fittedMonths: def.balance ? fit.fitted.length : fitInput.length,
    },
    ready: true,
    unavailable: null,
  };
}
